import { ReactNode, useId } from 'react'
import MuiAccordion from '@mui/material/Accordion'
import { AccordionDetails, AccordionSummary } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  primaryColors,
  transparentColors
} from '@assets/styles/helpers/colors.styles'
import { accordionDetailsStyles, accordionstyles } from './Accordion.styles'
import styles from './Accordion.module.scss'

export interface AccordionProps {
  title: string
  /** Controlled: the owner decides how many groups may be open at once. */
  expanded: boolean
  onToggle: () => void
  children: ReactNode
}

/**
 * One collapsible group, drawn the way the menu sidebar's groups are drawn.
 *
 * The styling was already written for those; this only puts it behind a
 * component so a second panel does not have to restate it.
 */
function Accordion({ title, expanded, onToggle, children }: AccordionProps) {
  /*
    MUI reads these two off the summary to name the region it expands. Without
    them the group is announced as a button that controls nothing, and the panel
    as a region with no name.
  */
  const uid = useId()

  return (
    <MuiAccordion
      expanded={expanded}
      onChange={onToggle}
      /*
        A closed group is not rendered at all, rather than rendered and hidden.
        Its content can be expensive: the font picker inside one of these asks
        Google for fifteen families, and a group nobody has opened should cost
        nothing.
      */
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ ...accordionstyles, background: transparentColors.whiteShade1 }}
    >
      <AccordionSummary
        id={`${uid}-header`}
        aria-controls={`${uid}-content`}
        // The header is a ButtonBase, and this app answers with colour rather
        // than with an ink ripple (§3).
        disableRipple
        expandIcon={<ExpandMoreIcon sx={{ color: primaryColors.white }} />}
      >
        <p className={`body-bold-medium ${styles.title}`}>{title}</p>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <div className={styles.content}>{children}</div>
      </AccordionDetails>
    </MuiAccordion>
  )
}

export default Accordion
